import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { AttentionList, IosInstallHint, PageHeader } from './MobileStates';
import { scopeHeaders } from '../utils/scopeHeaders';
import QuickInvoicePhoto from './QuickInvoicePhoto';
import { tone } from '../utils/tone';
import { apiBase } from '../../utils/authHeaders';
import { 
  Home, Package, Calendar, Truck, Shield, Users, 
  AlertTriangle, TrendingUp, Bell, Settings, User,
  Wifi, WifiOff, RefreshCw, CheckCircle, Clock
} from 'lucide-react';

const MobileDashboard = () => {
  const { user, token, role, isOnline, canAccessFeature, getMobileNavigation } = useMobileAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [role]);

  const fetchDashboardData = async () => {
    try {
      
      const response = await fetch(`${apiBase()}/api/mobile/dashboard`, {
        headers: scopeHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const getRoleBasedWidgets = () => {
    const widgets = [];

    // Common widgets for all roles
    widgets.push(
      <div key="status" className="bg-surface rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-ink">Connection Status</h3>
          {isOnline ? <Wifi className="w-5 h-5 text-primary-500" /> : <WifiOff className="w-5 h-5 text-danger" />}
        </div>
        <p className="text-sm text-ink-muted">
          {isOnline ? 'Connected and synced' : 'Offline - changes will sync when online'}
        </p>
      </div>
    );

    // Role-specific widgets
    if (canAccessFeature('inventory')) {
      widgets.push(
        <div key="inventory" className="bg-primary-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-primary-900">Inventory</h3>
            <Package className="w-5 h-5 text-primary-600" />
          </div>
          <div className="space-y-1">
            <p className="font-display text-2xl font-bold text-primary-900">{dashboardData.lowStockItems || 0}</p>
            <p className="text-sm text-primary-700">Low stock items</p>
            {dashboardData.expiringItems > 0 && (
              <div className="flex items-center mt-2">
                <AlertTriangle className="w-4 h-4 text-accent-600 mr-1" />
                <span className="text-sm text-accent-800">{dashboardData.expiringItems} expiring soon</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (canAccessFeature('bookings')) {
      widgets.push(
        <div key="bookings" className="bg-primary-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-primary-900">Bookings</h3>
            <Calendar className="w-5 h-5 text-primary-600" />
          </div>
          <div className="space-y-1">
            <p className="font-display text-2xl font-bold text-primary-900">{dashboardData.todayBookings || 0}</p>
            <p className="text-sm text-primary-700">Today's bookings</p>
            <p className="text-sm text-primary-600">{dashboardData.availableRooms || 0} rooms available</p>
          </div>
        </div>
      );
    }

    if (canAccessFeature('delivery')) {
      widgets.push(
        <div key="delivery" className="bg-accent-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-accent-800">Delivery</h3>
            <Truck className="w-5 h-5 text-accent-600" />
          </div>
          <div className="space-y-1">
            <p className="font-display text-2xl font-bold text-accent-800">{dashboardData.activeDeliveries || 0}</p>
            <p className="text-sm text-accent-800">Active deliveries</p>
            <p className="text-sm text-accent-600">{dashboardData.availableDrivers || 0} drivers available</p>
          </div>
        </div>
      );
    }

    if (canAccessFeature('safety')) {
      widgets.push(
        <div key="safety" className="bg-danger/5 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-danger">Safety</h3>
            <Shield className="w-5 h-5 text-danger" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-danger">
              {dashboardData.complianceStatus === 'NOT_ASSESSED' ? 'Not assessed' : `${dashboardData.complianceRate}%`}
            </p>
            <p className="text-sm text-danger">Compliance status</p>
            {dashboardData.pendingTasks > 0 && (
              <div className="flex items-center mt-2">
                <Clock className="w-4 h-4 text-accent-600 mr-1" />
                <span className="text-sm text-accent-800">{dashboardData.pendingTasks} tasks pending</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (canAccessFeature('events')) {
      widgets.push(
        <div key="events" className="bg-primary-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-primary-900">Events</h3>
            <Users className="w-5 h-5 text-primary-600" />
          </div>
          <div className="space-y-1">
            <p className="font-display text-2xl font-bold text-primary-900">{dashboardData.upcomingEvents || 0}</p>
            <p className="text-sm text-primary-700">Upcoming events</p>
            <p className="text-sm text-primary-600">Proposal count not connected</p>
          </div>
        </div>
      );
    }

    if (canAccessFeature('analytics')) {
      widgets.push(
        <div key="analytics" className="bg-primary-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-primary-900">Analytics</h3>
            <TrendingUp className="w-5 h-5 text-primary-600" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-primary-900">Revenue not connected</p>
            <p className="text-sm text-primary-700">Today's revenue</p>
            <p className="text-sm text-primary-600">Occupancy not connected</p>
          </div>
        </div>
      );
    }

    return widgets;
  };

  const getQuickActions = () => {
    const actions = [];

    if (canAccessFeature('inventory')) {
      actions.push({
        name: 'Scan Item',
        icon: Package,
        color: 'blue',
        action: () => navigate('/mobile/inventory', { state: { action: 'scan' } })
      });
    }

    if (canAccessFeature('bookings')) {
      actions.push({
        name: 'New Booking',
        icon: Calendar,
        color: 'green',
        action: () => navigate('/mobile/bookings', { state: { action: 'new' } })
      });
    }

    if (canAccessFeature('delivery')) {
      actions.push({
        name: 'Start Delivery',
        icon: Truck,
        color: 'orange',
        action: () => navigate('/mobile/delivery', { state: { action: 'start' } })
      });
    }

    if (canAccessFeature('safety')) {
      actions.push({
        name: 'Log Temperature',
        icon: Shield,
        color: 'red',
        action: () => navigate('/mobile/safety', { state: { tab: 'temperature' } })
      });
    }

    return actions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-muted">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-muted">
      <PageHeader title="Home" />
      <IosInstallHint />
      <div className="mt-3"><QuickInvoicePhoto /></div>
      {/* What needs attention first, from endpoints that already exist. */}
      <section className="mt-3">
        <h2 className="px-4 text-xs uppercase text-ink-muted mb-1">Needs attention</h2>
        <AttentionList />
      </section>
      {/* Header */}
      <div className="bg-surface shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="font-display text-xl font-bold text-ink">Welcome back, {user?.name || user?.full_name || user?.displayName || user?.email}</h1>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              className={`p-2 rounded-full ${refreshing ? 'animate-spin' : ''}`}
              disabled={refreshing}
            >
              <RefreshCw className="w-5 h-5 text-ink-muted" />
            </button>
            <button className="p-2 rounded-full relative">
              <Bell className="w-5 h-5 text-ink-muted" />
              {dashboardData.unreadNotifications > 0 && (
                <span className="absolute top-0 right-0 w-2 h-2 bg-danger rounded-full"></span>
              )}
            </button>
            <button className="p-2 rounded-full">
              <User className="w-5 h-5 text-ink-muted" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 space-y-4">
        {/* Quick Actions */}
        {getQuickActions().length > 0 && (
          <div className="bg-surface rounded-lg p-4 shadow-sm">
            <h2 className="font-semibold text-ink mb-3">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              {getQuickActions().map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className={`flex flex-col items-center justify-center p-4 rounded-lg ${tone(action.color).soft} ${tone(action.color).hover} transition-colors`}
                  >
                    <Icon className={`w-6 h-6 ${tone(action.color).icon} mb-2`} />
                    <span className={`text-sm font-medium ${tone(action.color).strong}`}>{action.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Role-Based Widgets */}
        <div className="grid grid-cols-1 gap-4">
          {getRoleBasedWidgets()}
        </div>

        {/* Recent Activity */}
        <div className="bg-surface rounded-lg p-4 shadow-sm">
          <h2 className="font-semibold text-ink mb-3">Recent Activity</h2>
          <div className="space-y-3">
            {dashboardData.recentActivity?.length ? dashboardData.recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'success' ? 'bg-primary-400' :
                  activity.type === 'warning' ? 'bg-warn' :
                  activity.type === 'error' ? 'bg-danger' : 'bg-primary-500'
                }`}></div>
                <div className="flex-1">
                  <p className="text-sm text-ink">{activity.message}</p>
                  <p className="text-xs text-ink-muted">{activity.timestamp}</p>
                </div>
              </div>
            )) : (
              <p className="text-sm text-ink-muted">
                {dashboardData.recentActivityStatus === 'NOT_CONFIGURED' ? 'Activity feed not connected' : 'No recent activity'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileDashboard;
